import { yupResolver } from "@hookform/resolvers/yup"
import { useStripe } from "@stripe/react-stripe-js"
import { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import * as Yup from "yup"

import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import useCompanyAddress from "~/app/pages/payments/hooks/useCompanyAddress"
import useCreateSepaPaymentMethod from "./useCreateSepaPaymentMethod"
import useCreateSepaSetupIntent from "./useCreateSepaSetupIntent"

import { AddSepaFormValues } from "~/app/pages/payments/types"
import { useParams } from "react-router-dom"
import {
  CountryIsoCode,
  CurrencyEnum,
  PaymentMethodsCustomerBankAccountEnumType,
} from "@oysterhr/schema"
import { getSubdivisions } from "~/app/queries/useSubdivisions"
import { useEffectOnce } from "@oysterhr/common"
import { useApolloClient } from "@apollo/client"
import { useConfiguredToasts, useReusableWizard } from "@oysterhr/feature-components"
import { captureException, captureMessage } from "@sentry/react"
import { PaymentMethodCategoryEnumType } from "../../../../enums"
import { useNewCompanyPaymentMethods } from "../../../../hooks/useNewCompanyPaymentMethods"
import { useUpdateCustomerBankAccount } from "../../../../hooks/useUpdateCustomerBankAccount"
import { useCreateCustomerBankAccount } from "../../../../hooks/useCreateCustomerBankAccount"
import { useCompanyPaymentsSettingsContext } from "../../../../contexts/CompanyPaymentsSettingsContext"

export const useNewAddSepaPaymentMethodForm = () => {
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage")

  const stripe = useStripe()
  const apolloClient = useApolloClient()
  const { goToNext } = useReusableWizard()
  const { paymentMethodType } = useParams()
  const { paymentsMandateSetup } = useCompanyPaymentsSettingsContext()
  const [confirmingSepaDirectDebit, setConfirmingSepaDirectDebit] = useState(false)
  const { displayErrorToast } = useConfiguredToasts()
  const [createSepaPaymentMethod, { loading: creatingSepaPaymentMethod }] =
    useCreateSepaPaymentMethod()
  const [createCustomerBankAccount, { loading: creatingCustomerBankAccount }] =
    useCreateCustomerBankAccount()
  const [createSepaSetupIntent, { data: sepaSetupIntentData }] = useCreateSepaSetupIntent()
  const { accountAspect: companyId } = useAccountAspect()
  const [updateCustomerBankAccount] = useUpdateCustomerBankAccount()
  const { data: companyAddressData, loading: companyAddressLoading } = useCompanyAddress({
    variables: { companyId },
  })
  const { data: companyPaymentMethodsData } = useNewCompanyPaymentMethods({
    variables: { companyId },
  })

  const customerBankAccount = companyPaymentMethodsData?.company.customerBankAccount
  const stripeClientSecret = sepaSetupIntentData?.createSepaSetupIntent.setupIntent?.clientSecret

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      name: Yup.string()
        .trim()
        .required(t("validation.required", { field: t("modal.accountHolderName") }))
        .min(3, t("validation.accountHolderName"))
        .max(100, t("validation.accountHolderName")),
      email: Yup.string()
        .email(t("validation.email"))
        .required(t("validation.required", { field: t("modal.email") })),
      iban: Yup.string().required(t("validation.required", { field: t("modal.iban") })),
      swiftBicCode: Yup.string().when([], {
        is: () => paymentMethodType === PaymentMethodCategoryEnumType.Fte,
        then: Yup.string()
          .required(t("validation.required", { field: t("modal.swiftBicCode") }))
          .matches(
            /^([A-Za-z]){4}([A-Za-z]){2}([0-9A-Za-z]){2}([0-9A-Za-z]{3})?$/,
            t("validation.bicFormat"),
          ),
        otherwise: Yup.string(),
      }),
      addressLine1: Yup.string()
        .max(17, t("validation.maxLength", { length: 17 }))
        .trim()
        .required(t("validation.required", { field: t("modal.addressLine1") })),
      addressLine2: Yup.string()
        .max(17, t("validation.maxLength", { length: 17 }))
        .trim(),
      postalCode: Yup.string()
        .max(35, t("validation.maxLength", { length: 35 }))
        .trim()
        .required(t("validation.required", { field: t("modal.postalCode") })),
      city: Yup.string()
        .max(35, t("validation.maxLength", { length: 35 }))
        .trim()
        .required(t("validation.required", { field: t("modal.city") })),
      region: Yup.string().when(["countryCode"], (countryCode: string, schema) => {
        return schema.test(
          "Region required test",
          t("validation.required"),
          async (subdivisionCode: string) => {
            const subdivsionsResponse = await getSubdivisions(apolloClient, countryCode || "")
            const isSubdivisionRequired = subdivsionsResponse?.isSubdivisionsArrayEmpty !== true

            return isSubdivisionRequired ? !!subdivisionCode : true
          },
        )
      }),
      countryCode: Yup.string()
        .trim()
        .required(t("validation.required", { field: t("modal.countryCode") })),
      isUserSigner: Yup.boolean(),
    })
  }, [apolloClient, paymentMethodType, t])

  const form = useForm({
    defaultValues: {
      name: "",
      iban: "",
      addressLine1: "",
      addressLine2: "",
      postalCode: "",
      city: "",
      region: "",
      countryCode: "",
      swiftBicCode: "",
      isUserSigner: true,
      email: "",
    },
    resolver: yupResolver(validationSchema),
  })

  useEffectOnce(() => {
    const effect = async () => {
      try {
        await createSepaSetupIntent({
          variables: { companyId: companyId },
          onCompleted: data => {
            if (data.createSepaSetupIntent.errors.length) {
              displayErrorToast({
                content: data.createSepaSetupIntent.errors.join(", "),
              })
            }
          },
        })
      } catch (err) {
        captureException(err)
      }
    }

    if (paymentMethodType === PaymentMethodCategoryEnumType.Contractor) {
      effect()
    }
  })

  const onSubmit = async (values: AddSepaFormValues) => {
    if (paymentMethodType === PaymentMethodCategoryEnumType.Contractor) {
      // NOTE: We delay creation of contractor payment method until last step
      // so we don't need update here.
      return createPaymentMethod(values)
    } else {
      return customerBankAccount
        ? updateFTEPaymentMethod(customerBankAccount.id, values)
        : createFTEPaymentMethod(values)
    }
  }

  // ------ FTE ------
  const updateFTEPaymentMethod = async (
    customerBankAccountId: string,
    values: AddSepaFormValues,
  ) => {
    return await updateCustomerBankAccount({
      variables: {
        customerBankAccountId,
        billingAddress: {
          addressLine1: values.addressLine1 ?? "",
          addressLine2: values.addressLine2,
          countryCode: values.countryCode ?? "",
          city: values.city ?? "",
          postalCode: values.postalCode ?? "",
          region: values.region ?? "",
        },
        bankDetails: {
          sepa: {
            accountHolderName: values.name,
            countryCode: values.countryCode as CountryIsoCode,
            currencyCode: CurrencyEnum.Eur,
            email: values.email,
            iban: values.iban,
            swiftBicCode: values.swiftBicCode ?? "",
          },
        },
        userNotSigner: !values.isUserSigner,
      },
      onCompleted: data => {
        if (data.updateCustomerBankAccount.errors.length) {
          displayErrorToast({
            content: data.updateCustomerBankAccount.errors.join(", "),
          })
        } else {
          goToNext()
        }
      },
    })
  }
  const createFTEPaymentMethod = async (values: AddSepaFormValues) => {
    return await createCustomerBankAccount({
      variables: {
        ownerId: companyId,
        paymentMethodType: PaymentMethodsCustomerBankAccountEnumType.Sepa,
        billingAddress: {
          addressLine1: values.addressLine1 ?? "",
          addressLine2: values.addressLine2,
          countryCode: values.countryCode ?? "",
          city: values.city ?? "",
          postalCode: values.postalCode ?? "",
          region: values.region ?? "",
        },
        bankDetails: {
          sepa: {
            accountHolderName: values.name,
            countryCode: values.countryCode as CountryIsoCode,
            currencyCode: CurrencyEnum.Eur,
            email: values.email,
            iban: values.iban,
            swiftBicCode: values.swiftBicCode ?? "",
          },
        },
        userNotSigner: !values.isUserSigner,
      },
      onCompleted: data => {
        if (data.createCustomerBankAccount.errors.length) {
          displayErrorToast({
            content: data.createCustomerBankAccount.errors.join(", "),
          })
        } else {
          goToNext()
        }
      },
    })
  }

  // ------ Contractor ------
  const getSepaDebitSetupIntent = async (values: AddSepaFormValues) => {
    if (!stripe || !stripeClientSecret) {
      throw new Error("Stripe isn't ready")
    }

    const company = companyAddressData?.company
    const address = company?.billingAddress || company?.address
    const sepaAddress = {
      addressLine1: values.addressLine1,
      addressLine2: values.addressLine2,
      postalCode: values.postalCode,
      countryCode: values.countryCode,
      region: values.region,
      city: values.city,
    }

    // If mandate setup feature flag is enabled we use the new values
    const billingAddress = paymentsMandateSetup ? sepaAddress : address
    setConfirmingSepaDirectDebit(true)

    const { setupIntent, error } = await stripe.confirmSepaDebitSetup(stripeClientSecret, {
      payment_method: {
        sepa_debit: { iban: values.iban },
        billing_details: {
          name: values.name,
          email: values.email,
          address: {
            line1: billingAddress?.addressLine1,
            line2: billingAddress?.addressLine2,
            postal_code: billingAddress?.postalCode,
            country: billingAddress?.countryCode,
            city: billingAddress?.city,
            state: billingAddress?.region,
          },
        },
      },
    })
    setConfirmingSepaDirectDebit(false)

    if (error) {
      captureMessage(error.message || "Couldn't submit SEPA direct debit details", {
        extra: {
          ...error,
        },
      })
      throw new Error(error.message)
    }

    return setupIntent
  }
  const createPaymentMethod = async (values: AddSepaFormValues) => {
    try {
      const setupIntent = await getSepaDebitSetupIntent(values)

      return await createSepaPaymentMethod({
        variables: { ownerId: companyId, setupIntentId: setupIntent?.id },
        onCompleted: data => {
          if (data.createPaymentMethodStripeSepa.errors.length) {
            displayErrorToast({
              content: data.createPaymentMethodStripeSepa.errors.join(", "),
            })
          } else {
            goToNext()
          }
        },
      })
    } catch (err) {
      displayErrorToast({
        content: (err as Error).message,
      })
      console.error(err)
      throw err
    }
  }

  return {
    ...form,
    // FIXME: Come up with better approach to handle loading of requests made
    // before the creation
    loading: creatingSepaPaymentMethod || confirmingSepaDirectDebit || creatingCustomerBankAccount,
    onSubmit,
    formIsReady:
      !companyAddressLoading &&
      // NOTE: If FTE, don't care about stripe
      (paymentMethodType === PaymentMethodCategoryEnumType.Fte ||
        (!!stripeClientSecret && !!stripe)),
  }
}
